import { useQuery } from "../_app/lib/query";
import { BarControllerChartOptions, ChartOptions, CoreScaleOptions, Scale, Tick, TooltipItem, TooltipModel } from "chart.js";
import { AjaxResponse } from "../_app/api";
import {
  getCallCost,
  getCallCount,
  getCallDuration,
  getCostByProduct,
  getCostByUsageType,
  getCostUser,
  getDataUser,
  getNonGeographic,
  getServiceChargesFrequency,
  getServiceCostByProduct,
  getServiceCostByType,
  getUsageBreakdown,
  getUsageSummary,
} from "./api";
import { PeriodParam, Query } from "../filter/types";
import { useContextProvider } from "../context/providers/ContextProvider";

export function useUsageSummary(period?: PeriodParam, options = {}) {
  const { contextId: accountId } = useContextProvider();
  return useQuery<any, AjaxResponse>({
    queryKey: ["usageSummary", { accountId, period }],
    queryFn: () => getUsageSummary(accountId, period),
    ...options,
  });
}

export function useUsageBreakdown(queries: Query[], options = {}) {
  const { contextId: accountId } = useContextProvider();
  return useQuery<any, AjaxResponse>({
    queryKey: ["usageBreakdown", { accountId, queries }],
    queryFn: () => getUsageBreakdown(accountId, queries),
    ...options,
  });
}

export function useServiceChargesFrequency(queries: Query[], options = {}) {
  const { contextId: accountId } = useContextProvider();
  return useQuery<any, AjaxResponse>({
    queryKey: ["serviceChargesFrequency", { accountId, queries }],
    queryFn: () => getServiceChargesFrequency(accountId, queries),
    ...options,
  });
}

export function useCostByUsageType(queries: Query[], options = {}) {
  const { contextId: accountId } = useContextProvider();
  return useQuery<any, AjaxResponse>({
    queryKey: ["costByUsageType", { accountId, queries }],
    queryFn: () => getCostByUsageType(accountId, queries),
    ...options,
  });
}

export function useServiceCostByType(queries: Query[], options = {}) {
  const { contextId: accountId } = useContextProvider();
  return useQuery<any, AjaxResponse>({
    queryKey: ["serviceCostByType", { accountId, queries }],
    queryFn: () => getServiceCostByType(accountId, queries),
    ...options,
  });
}

export function useCostByProduct(queries: Query[], options = {}) {
  const { contextId: accountId } = useContextProvider();
  return useQuery<any, AjaxResponse>({
    queryKey: ["costByProduct", { accountId, queries }],
    queryFn: () => getCostByProduct(accountId, queries),
    ...options,
  });
}

export function useServiceCostByProduct(queries: Query[], options = {}) {
  const { contextId: accountId } = useContextProvider();
  return useQuery<any, AjaxResponse>({
    queryKey: ["serviceCostByProduct", { accountId, queries }],
    queryFn: () => getServiceCostByProduct(accountId, queries),
    ...options,
  });
}

export function useCostUser(queries: Query[], options = {}) {
  const { contextId: accountId } = useContextProvider();
  return useQuery<any, AjaxResponse>({
    queryKey: ["topCostUser", { accountId, queries }],
    queryFn: () => getCostUser(accountId, queries),
    ...options,
  });
}

export function useDataUser(queries: Query[], options = {}) {
  const { contextId: accountId } = useContextProvider();
  return useQuery<any, AjaxResponse>({
    queryKey: ["topDataUser", { accountId, queries }],
    queryFn: () => getDataUser(accountId, queries),
    ...options,
  });
}

export function useCallCount(queries: Query[], options = {}) {
  const { contextId: accountId } = useContextProvider();
  return useQuery<any, AjaxResponse>({
    queryKey: ["callCount", { accountId, queries }],
    queryFn: () => getCallCount(accountId, queries),
    ...options,
  });
}

export function useCallDuration(queries: Query[], options = {}) {
  const { contextId: accountId } = useContextProvider();
  return useQuery<any, AjaxResponse>({
    queryKey: ["callDuration", { accountId, queries }],
    queryFn: () => getCallDuration(accountId, queries),
    ...options,
  });
}

export function useCallCost(queries: Query[], options = {}) {
  const { contextId: accountId } = useContextProvider();
  return useQuery<any, AjaxResponse>({
    queryKey: ["callCost", { accountId, queries }],
    queryFn: () => getCallCost(accountId, queries),
    ...options,
  });
}

export function useNonGeographic(options = {}) {
  return useQuery<any, AjaxResponse>({
    queryKey: ["topNonGeographic"],
    queryFn: getNonGeographic,
    ...options,
  });
}

export function useLinesSpend(queries: Query[], options = {}) {
  const { contextId: accountId } = useContextProvider();
  return useQuery<any, AjaxResponse>({
    queryKey: ["topLinesSpend", { accountId, queries }],
    queryFn: () => getDataUser(accountId, queries),
    ...options,
  });
}

export function useDestinations(queries: Query[], options = {}) {
  const { contextId: accountId } = useContextProvider();
  return useQuery<any, AjaxResponse>({
    queryKey: ["topDestinations", { accountId, queries }],
    queryFn: () => getDataUser(accountId, queries),
    ...options,
  });
}

/* ChartJS Hooks */

export function useVerticalBarChartOptions(
  unit: string = "",
  yAxisLabelCallback?: Function,
  tooltipLabelCallback?: Function,
  overrides = {},
) {
  const yAxisLabel =
    yAxisLabelCallback ||
    function (value: any) {
      return unit + value;
    };
  const tooltipLabel =
    tooltipLabelCallback ||
    function (context: any) {
      return unit + context?.formattedValue;
    };
  return {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        align: "start",
        labels: {
          usePointStyle: true,
          padding: 36,
        },
      },
      title: {
        display: false,
      },
      tooltip: {
        position: "nearest",
        backgroundColor: "#ffffff",
        titleColor: "#000000",
        bodyColor: "#000000",
        displayColors: false,
        padding: 15,
        borderColor: "#000000",
        caretSize: 0,
        borderWidth: 1,
        callbacks: {
          label: tooltipLabel,
          title: function (context: any) {
            return context?.[0]?.dataset?.label || "";
          },
        },
      },
    },
    interaction: {
      mode: "point",
    },
    scales: {
      y: {
        ticks: {
          callback: yAxisLabel,
        },
      },
    },
    ...overrides,
  } as BarControllerChartOptions;
}

export function useHorizontalBarChartOptions(
  unit: string = "",
  xAxisLabelCallback?: Function,
  tooltipLabelCallback?: Function,
  stacked = false,
  overrides = {},
) {
  const xAxisLabel =
    xAxisLabelCallback ||
    function (value: any) {
      return unit + value;
    };
  const tooltipLabel =
    tooltipLabelCallback ||
    function (context: any) {
      return unit + context?.formattedValue;
    };
  return {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        align: "start",
        labels: {
          usePointStyle: true,
          padding: 36,
        },
      },
      title: {
        display: false,
      },
      tooltip: {
        position: "nearest",
        backgroundColor: "#ffffff",
        titleColor: "#000000",
        bodyColor: "#000000",
        displayColors: false,
        padding: 15,
        borderColor: "#000000",
        caretSize: 0,
        borderWidth: 1,
        callbacks: {
          label: tooltipLabel,
          title: function (context: any) {
            return context?.[0]?.dataset?.label;
          },
        },
      },
    },
    skipNull: true,
    interaction: {
      mode: "point",
    },
    scales: {
      y: {
        stacked: stacked,
      },
      x: {
        stacked: stacked,
        ticks: {
          callback: xAxisLabel,
        },
      },
    },
    ...overrides,
  } as BarControllerChartOptions;
}

export function useLineChartOptions(
  unit: string = "",
  xAxisLabelCallback?: (
    this: Scale<CoreScaleOptions>,
    tickValue: string | number,
    index: number,
    ticks: Tick[],
  ) => string | number | string[] | number[] | null | undefined,
  tooltipLabelCallback?: (this: TooltipModel<"line">, tooltipItem: TooltipItem<"line">) => string | void | string[],
  stacked = false,
  overrides = {},
) {
  const xAxisLabel =
    xAxisLabelCallback ||
    function (value: any) {
      return unit + value;
    };
  const tooltipLabel =
    tooltipLabelCallback ||
    function (context: any) {
      return [context.label, context.dataset.label + " " + unit + context.formattedValue];
    };
  return {
    type: "line",
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        align: "start",
        labels: {
          usePointStyle: true,
          padding: 35,
        },
      },
      title: {
        display: false,
      },
      tooltip: {
        position: "nearest",
        backgroundColor: "#ffffff",
        titleColor: "#000000",
        bodyColor: "#000000",
        padding: { left: 25, right: 25, top: 15, bottom: 15 },
        bodyFont: { weight: "bold" },
        caretSize: 0,
        displayColors: false,
        borderColor: "#000000",
        borderWidth: 1,
        callbacks: {
          label: tooltipLabel,
          title: () => "",
        },
      },
    },
    interaction: {
      mode: "point",
    },
    scales: {
      xAxes: {
        stacked: true,
      },
      yAxes: {
        stacked: false,
        ticks: {
          callback: xAxisLabel,
        },
      },
    },
    ...overrides,
  } as ChartOptions<"line">;
}
